import { 
  SyncOutlined, 
  SendOutlined, 
  PrinterOutlined, 
  CheckCircleOutlined,
  ShoppingOutlined,
  BugOutlined
} from '@ant-design/icons';

/**
 * Get display information for different task types
 */
export const getTaskDisplayInfo = (task) => {
  const taskType = task.data?.taskType || 'unknown';
  const taskName = task.data?.taskName || task.name;
  
  const taskMap = {
    sync: {
      icon: <SyncOutlined style={{ color: '#1890ff' }} />,
      title: 'Sync Tasks',
      category: 'Data Sync'
    },
    orders: {
      icon: <ShoppingOutlined style={{ color: '#722ed1' }} />,
      title: 'Order Sync',
      category: 'Orders'
    },
    mockup: {
      icon: <SendOutlined style={{ color: '#52c41a' }} />,
      title: 'Send Mockup',
      category: 'Communication'
    },
    shipment: {
      icon: <CheckCircleOutlined style={{ color: '#fa8c16' }} />,
      title: 'Confirm Shipment',
      category: 'Fulfillment'
    },
    printer: {
      icon: <PrinterOutlined style={{ color: '#13c2c2' }} />,
      title: 'Fulfill to Printer',
      category: 'Production'
    }
  };

  const info = taskMap[taskType] || {
    icon: <BugOutlined style={{ color: '#d9d9d9' }} />,
    title: taskName || 'Unknown Task',
    category: 'System'
  };

  // Add extra info based on task data
  let extraInfo = '';
  if (task.data?.orderItemIds?.length) {
    extraInfo = `${task.data.orderItemIds.length} items`;
  } else if (task.data?.totalOrderItems) {
    extraInfo = `${task.data.totalOrderItems} items`;
  }

  return {
    ...info,
    extraInfo
  };
};

/**
 * Format timestamp to relative time
 */
export const formatTimeAgo = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInMs = now - time;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else {
    return `${diffInDays}d ago`;
  }
};

/**
 * Calculate task progress percentage
 */
export const calculateProgress = (task) => {
  if (task.progress !== undefined && task.progress !== null) {
    return Math.round(task.progress);
  }
  
  // Estimate progress based on status and timestamps
  if (task.status === 'completed') {
    return 100;
  } else if (task.status === 'failed') {
    return 0;
  } else if (task.status === 'active' && task.processedOn) {
    // Estimate based on processing time (simple heuristic)
    const processingTime = new Date() - new Date(task.processedOn);
    const estimatedDuration = 60000; // Assume 1 minute average task duration
    const progress = Math.min(90, (processingTime / estimatedDuration) * 100);
    return Math.round(progress);
  }
  
  return 0;
};

/**
 * Get task category color
 */
export const getTaskCategoryColor = (taskType) => {
  const colorMap = {
    sync: '#1890ff',
    orders: '#722ed1', 
    mockup: '#52c41a',
    shipment: '#fa8c16',
    printer: '#13c2c2'
  };
  
  return colorMap[taskType] || '#d9d9d9';
};

/**
 * Get task priority level
 */
export const getTaskPriority = (task) => {
  // Priority can be determined from task data or job options
  if (task.opts?.priority !== undefined) {
    return task.opts.priority;
  }
  
  // Default priorities by task type
  const priorityMap = {
    shipment: 5,
    mockup: 4,
    printer: 3,
    orders: 2,
    sync: 1
  };
  
  const taskType = task.data?.taskType || 'unknown';
  return priorityMap[taskType] || 0;
};

/**
 * Check if task should show detailed info
 */
export const shouldShowDetails = (task) => {
  return task.status === 'failed' || 
         task.status === 'active' || 
         (task.data && Object.keys(task.data).length > 2);
};

/**
 * Format task duration
 */
export const formatDuration = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const durationMs = end - start;
  
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Get status display text
 */
export const getStatusDisplayText = (status) => {
  const statusMap = {
    waiting: 'Queued',
    active: 'Running',
    completed: 'Done',
    failed: 'Error',
    delayed: 'Delayed'
  };
  
  return statusMap[status] || status;
};
